/**
 * HostPoint POS - Idle Timeout Settings Component
 * UI for configuring auto-logout timeout
 */

import { Clock, Shield } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useIdleTimeoutSettings, IDLE_TIMEOUT_OPTIONS } from '@/lib/idle-timeout';

export function IdleTimeoutSettings() {
  const { setting, updateSetting } = useIdleTimeoutSettings();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Session Security
        </CardTitle>
        <CardDescription>
          Configure automatic logout for inactive sessions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="idle-timeout" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Auto-logout after inactivity
          </Label>
          <Select value={setting} onValueChange={updateSetting}>
            <SelectTrigger id="idle-timeout" className="w-full">
              <SelectValue placeholder="Select timeout" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(IDLE_TIMEOUT_OPTIONS).map(([key, option]) => (
                <SelectItem key={key} value={key}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {setting === 'never' 
              ? 'Sessions will not automatically expire. Not recommended for shared devices.'
              : `You will be automatically logged out after ${IDLE_TIMEOUT_OPTIONS[setting]?.label} of inactivity.`
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Compact inline settings for POS dropdown
 */
export function IdleTimeoutSettingsInline({ className }) {
  const { setting, updateSetting } = useIdleTimeoutSettings();

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-4">
        <Label className="text-sm flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Auto-logout
        </Label>
        <Select value={setting} onValueChange={updateSetting}>
          <SelectTrigger className="w-32 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(IDLE_TIMEOUT_OPTIONS).map(([key, option]) => (
              <SelectItem key={key} value={key}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default IdleTimeoutSettings;
